import React, { useEffect, useRef, useState } from 'react';
import { getProductImage } from '../api/productApi';
import styles from './ProductImage.module.css';

const ProductImage = ({
  productId,
  hasImage,
  alt,
  className,
  placeholderClassName,
  loadingLabel = 'Loading image...',
  noImageLabel = 'No Image',
  errorLabel = 'Image unavailable',
  lazy = true,
}) => {
  const placeholderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [imageState, setImageState] = useState({
    status: hasImage ? 'loading' : 'empty',
    src: '',
  });

  useEffect(() => {
    if (!productId || !hasImage) {
      setShouldLoad(false);
      setImageState({ status: 'empty', src: '' });
      return undefined;
    }

    setImageState({ status: 'loading', src: '' });

    if (!lazy || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    setShouldLoad(false);
    const node = placeholderRef.current;
    if (!node) {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [productId, hasImage, lazy]);

  useEffect(() => {
    if (!productId || !hasImage || !shouldLoad) return undefined;

    let isMounted = true;
    let objectUrl = '';
    setImageState({ status: 'loading', src: '' });

    getProductImage(productId)
      .then((response) => {
        objectUrl = URL.createObjectURL(response.data);
        if (isMounted) {
          setImageState({ status: 'loaded', src: objectUrl });
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      })
      .catch((error) => {
        if (!isMounted) return;
        const status = error?.response?.status === 404 ? 'empty' : 'error';
        setImageState({ status, src: '' });
      });

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [productId, hasImage, shouldLoad]);

  if (imageState.status === 'loaded') {
    return <img src={imageState.src} alt={alt} className={className} />;
  }

  if (imageState.status === 'loading') {
    return (
      <div ref={placeholderRef} className={`${placeholderClassName || ''} ${styles.imageState}`}>
        <span className={styles.imageSpinner} aria-hidden="true" />
        <span>{loadingLabel}</span>
      </div>
    );
  }

  return (
    <div ref={placeholderRef} className={`${placeholderClassName || ''} ${styles.imageState}`}>
      {imageState.status === 'error' ? errorLabel : noImageLabel}
    </div>
  );
};

export default ProductImage;
